import React from 'react'

import ErrorShownPic from '../../assets/images/error-fallback/errorImage.png'
import Stack from '@mui/material/Stack'
import './ErrorFallback.styles.scss'
import Box from '@mui/material/Box'
import { logger } from '../../helpers/logger'
import { captureException } from '@sentry/react'

type ErrorBoundariesProps = { children: JSX.Element | JSX.Element[], componentName?: string }

type ErrorBoundariesState = {
    hasError: boolean
}

export const DefaultErrorFallback = () => {
    return (
        <Stack
            id="errorfallback__stack"
            alignItems="center"
            sx={{ width: '100%' }}
        >
            <img
                src={ErrorShownPic}
                alt="web site broke down img"
                id="errorfallback__ErrorShownPic"
            />
            <Box fontFamily="Lexend Deca" id="errorfallback__fallbackInfo">
                Sorry, website is down temporarily. Looking forward to see you
                back soon.
            </Box>
        </Stack>
    )
}

export default class ErrorBoundaries extends React.Component<
    ErrorBoundariesProps,
    ErrorBoundariesState
> {
    constructor(props: ErrorBoundariesProps) {
        super(props)

        this.state = {
            hasError: false,
        }
    }

    static getDerivedStateFromError(error: any) {
        return {
            hasError: true,
        }
    }

    componentDidCatch(error: any) {
        logger.debug('The errors that catched by error boundary:')
        logger.error(error)
        captureException(error, {
            tags: {
                feature: 'Error Boundary',
                component: this.props.componentName ?? 'ErrorFallback',
            },
        })
    }

    render() {
        if (this.state.hasError) {
            return <DefaultErrorFallback />
        }
        return this.props.children
    }
}


export const withErrorBoundary = <P extends object>(
    Component: React.ComponentType<P>,
    componentName: string
) => {
    const WrappedComponent = (props: P) => (
        <ErrorBoundaries componentName={componentName}>
            <Component {...props} />
        </ErrorBoundaries>
    )

    WrappedComponent.displayName = `withErrorBoundary(${componentName})`

    return WrappedComponent
}